/**
 * Date utilities for formatting and parsing.
 * 
 * Standardized to use dayjs for all operations to ensure consistency
 * between server and client rendering and avoid timezone-related bugs.
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'

// Initialize all plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(isBetween)

/** 
 * @deprecated Avoid using this. Use dayjs(date) for local or dayjs.utc(date) for UTC.
 * This was a hack to strip timezones which caused many bugs.
 */
export const Date_getLocal = (date: Date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return new Date()
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
}

/** Formats date as YYYY/MM/DD using the runtime's local timezone. */
export const Date_getYYYYMMDD = (date: Date | string): string => {
    if (!date) return '--'
    const d = dayjs(date)
    if (!d.isValid()) return '--'
    return d.format('YYYY/MM/DD')
}

/** Formats date as YYYY/MM/DD from a local date object. */
export const Date_getYYYYMMDDFromLocal = (local_date: Date) => {
    return Date_getYYYYMMDD(local_date)
}

/**
 * Returns one week key per calendar week that contains at least one match.
 * Each key is the start-of-week date (YYYY/MM/DD).
 */
export const Date_getWeeksFromDateArray = (matchDates: (Date | string)[]) => {
    if (!matchDates?.length) return []
    
    const weekStarts = new Set<string>()
    
    for (const d of matchDates) {
        const date = dayjs(d).utc()
        if (!date.isValid()) continue
        
        // Use Monday as start of week (default for many leagues)
        const startOfWeek = date.startOf('week')
        weekStarts.add(startOfWeek.format('YYYY/MM/DD'))
    }
    
    return Array.from(weekStarts).sort((a, b) => a.localeCompare(b))
}

/** Formats time as HH:mm (24h) using local or UTC time. */
export const Date_get24hHHMM = (date: Date | string, useUtc = false): string => {
    if (!date) return '--:--'
    const d = useUtc ? dayjs(date).utc() : dayjs(date)
    if (!d.isValid()) return '--:--'
    return d.format('HH:mm')
}

/** Converts a date to YYYY/MM/DD string for grouping. */
export const Date_toYMDString = (date: Date | string): string => {
    return Date_getYYYYMMDD(date)
}

export const Date_getDaysOfWeekFull = () => {
    return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
}

/** Returns the timestamp of a date. */
export const Date_getTime = (date: Date | string): number => {
    const d = dayjs(date)
    return d.isValid() ? d.valueOf() : 0
}

/** Checks if two dates are within a certain interval. */
export const Date_areWithinMinutesInterval = (date1: Date | string, date2: Date | string, minutes: number) => {
    const d1 = dayjs(date1)
    const d2 = dayjs(date2)
    if (!d1.isValid() || !d2.isValid()) return false
    return Math.abs(d1.diff(d2, 'minute')) < minutes
}

/** Checks if a date is within 24 hours of now. */
export const Date_isWithin24Hours = (date: Date | string): boolean => {
    const d = dayjs(date)
    if (!d.isValid()) return false
    return Math.abs(dayjs().diff(d, 'hour')) <= 24
}

/** Parses a date and time from form data (MM/DD/YYYY hh:mm A). */
export const Date_formDataDateAndTime = (date: string) => {
    const parsedDate = dayjs(date, 'MM/DD/YYYY hh:mm A')
    return parsedDate.isValid() ? parsedDate.toDate() : new Date()
}

/** Formats date with ordinal suffix (e.g., 4th May 2024). */
export const Date_formatVerbose = (date: Date | string): string => {
    if (!date) return 'Invalid date'
    const d = dayjs(date)
    if (!d.isValid()) return 'Invalid date'

    const day = d.date()
    const suffix = (day: number) => {
        if (day > 3 && day < 21) return 'th'
        switch (day % 10) {
            case 1: return 'st'
            case 2: return 'nd'
            case 3: return 'rd'
            default: return 'th'
        }
    }
    
    return `${day}${suffix(day)} ${d.format('MMMM YYYY')}`
}

/** Formats date as "Jan 20, 2025". */
export const Date_formatShort = (date: Date | string): string => {
    if (!date) return '—'
    const d = dayjs(date)
    if (!d.isValid()) return '—'
    return d.format('MMM D, YYYY')
}
